import { Divider, Typography, Paper, Chip, Table, TableBody, TableCell, TableHead, TableRow} from '@tunasong/ui-lib'
import styles from './content.module.css'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { TunaPluginContentProps } from '@tunasong/plugin-lib'
import type { GearRequirement, Persisted, Profile, Rider, RiderMember } from '@tunasong/schemas'
import { isRider } from '@tunasong/schemas'
import type { FC } from 'react'

export interface RiderContentProps extends TunaPluginContentProps<Rider> {
  edit?: boolean
}

/** Category labels for display */
const categoryLabels: Record<string, string> = {
  microphone: 'Mic',
  monitor: 'Mon',
  stand: 'Stand',
  instrument: 'Inst',
  backline: 'Backline',
  accessory: 'Acc',
  other: 'Other',
}

/** Render a single member's requirements */
const MemberRow: FC<{ member: RiderMember; profile?: Profile }> = ({ member, profile }) => {
  const name = profile?.name ?? profile?.email ?? member.userId
  return (
    <>
      <TableRow>
        <TableCell>
          <Typography className={styles.fontBold}>{member.channelNumber ?? '-'}</Typography>
        </TableCell>
        <TableCell>
          <Typography className={styles.fontBold}>{name}</Typography>
          {member.role ? (
            <Typography variant="body2" color="textSecondary">
              {member.role}
            </Typography>
          ) : null}
        </TableCell>
        <TableCell>Mix {member.monitorMix ?? '-'}</TableCell>
        <TableCell>
          {member.requirements.length > 0 ? (
            <div>
              {member.requirements.map((req, i) => (
                <div key={i} className={styles.requirementRow}>
                  <div className={styles.requirementInner}>
                    <Chip
                      size="small"
                      label={categoryLabels[req.category] ?? req.category}
                      color="primary"
                      variant="outlined"
                      className={styles.chipMinWidth}
                    />
                    <Typography variant="body2">
                      {req.quantity > 1 ? `${req.quantity}x ` : ''}
                      <strong>{req.name}</strong>
                    </Typography>
                    {req.providedByArtist ? (
                      <Chip size="small" label="Artist" variant="outlined" color="success" />
                    ) : null}
                  </div>
                  {req.notes ? (
                    <Typography variant="caption" color="textSecondary" className={styles.noteMargin}>
                      {req.notes}
                    </Typography>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <Typography variant="caption" color="textSecondary">
              No requirements
            </Typography>
          )}
        </TableCell>
      </TableRow>
    </>
  )
}

/** Render a gear requirement as a chip */
const RequirementChip: FC<{ requirement: GearRequirement }> = ({ requirement }) => (
  <Chip
    size="small"
    label={`${requirement.quantity > 1 ? `${requirement.quantity}x ` : ''}${requirement.name}`}
    variant={requirement.providedByArtist ? 'outlined' : 'filled'}
    title={requirement.notes}
  />
)

const RiderContent: FC<RiderContentProps> = props => {
  const { element } = props

  // Get profiles for all members
  const memberIds = element.members?.map(m => m.userId) ?? []
  const { profiles } = graphHooks.useProfiles({ userIds: memberIds })
  const profileMap = new Map(profiles.map(p => [p.userId, p]))

  if (!isRider(element)) {
    return null
  }

  const rider = element as Persisted<Rider>
  const monitors = rider.shared?.monitors

  return (
    <div className={styles.root}>
      {/* Header */}
      <div>
        <Typography variant="h5" gutterBottom>
          {rider.name}
        </Typography>
        {rider.eventName || rider.venue || rider.eventDate ? (
          <div className={styles.headerRow}>
            {rider.eventName ? <Typography color="textSecondary">{rider.eventName}</Typography> : null}
            {rider.venue ? <Typography color="textSecondary">{rider.venue}</Typography> : null}
            {rider.eventDate ? <Typography color="textSecondary">{rider.eventDate}</Typography> : null}
          </div>
        ) : null}
      </div>

      <Divider />

      {/* Input List / Member Requirements */}
      <Paper variant="outlined">
        <div className={styles.sectionPadding}>
          <Typography variant="h6" gutterBottom>
            Input List
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={60}>Ch</TableCell>
                <TableCell>Source</TableCell>
                <TableCell width={100}>Monitor</TableCell>
                <TableCell>Requirements</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rider.members?.map((member, i) => (
                <MemberRow key={i} member={member} profile={profileMap.get(member.userId)} />
              ))}
            </TableBody>
          </Table>
        </div>
      </Paper>

      {/* Monitor Requirements */}
      {monitors ? (
        <Paper variant="outlined">
          <div className={styles.sectionPadding}>
            <Typography variant="h6" gutterBottom>
              Monitor Requirements
            </Typography>
            <div className={styles.monitorRow}>
              {monitors.wedges ? (
                <div>
                  <Typography variant="body2" color="textSecondary">
                    Wedges
                  </Typography>
                  <Typography variant="h6">{monitors.wedges}</Typography>
                </div>
              ) : null}
              {monitors.iemPacks ? (
                <div>
                  <Typography variant="body2" color="textSecondary">
                    IEM Packs
                  </Typography>
                  <Typography variant="h6">{monitors.iemPacks}</Typography>
                </div>
              ) : null}
              {monitors.mixes ? (
                <div>
                  <Typography variant="body2" color="textSecondary">
                    Mixes
                  </Typography>
                  <Typography variant="h6">{monitors.mixes}</Typography>
                </div>
              ) : null}
            </div>
          </div>
        </Paper>
      ) : null}

      {/* Backline */}
      {rider.shared?.backline?.length ? (
        <Paper variant="outlined">
          <div className={styles.sectionPadding}>
            <Typography variant="h6" gutterBottom>
              Backline
            </Typography>
            <div className={styles.backlineChips}>
              {rider.shared.backline.map((req, i) => (
                <RequirementChip key={i} requirement={req} />
              ))}
            </div>
          </div>
        </Paper>
      ) : null}

      {/* Stage Requirements */}
      {rider.shared?.stageSize || rider.shared?.power ? (
        <Paper variant="outlined">
          <div className={styles.sectionPadding}>
            <Typography variant="h6" gutterBottom>
              Stage Requirements
            </Typography>
            <div className={styles.stageRow}>
              {rider.shared?.stageSize ? (
                <div>
                  <Typography variant="body2" color="textSecondary">
                    Minimum Stage Size
                  </Typography>
                  <Typography>
                    {rider.shared.stageSize.width}m x {rider.shared.stageSize.depth}m
                  </Typography>
                </div>
              ) : null}
              {rider.shared?.power ? (
                <div>
                  <Typography variant="body2" color="textSecondary">
                    Power
                  </Typography>
                  <Typography>{rider.shared.power}</Typography>
                </div>
              ) : null}
            </div>
          </div>
        </Paper>
      ) : null}

      {/* Hospitality */}
      {rider.hospitality ? (
        <Paper variant="outlined">
          <div className={styles.sectionPadding}>
            <Typography variant="h6" gutterBottom>
              Hospitality
            </Typography>
            <div className={styles.hospitalityRow}>
              {rider.hospitality.meals ? (
                <div>
                  <Typography variant="body2" color="textSecondary">
                    Meals
                  </Typography>
                  <Typography variant="h6">{rider.hospitality.meals}</Typography>
                </div>
              ) : null}
              {rider.hospitality.parkingSpaces ? (
                <div>
                  <Typography variant="body2" color="textSecondary">
                    Parking
                  </Typography>
                  <Typography variant="h6">{rider.hospitality.parkingSpaces}</Typography>
                </div>
              ) : null}
            </div>
            {rider.hospitality.drinks?.length ? (
              <div className={styles.drinksSection}>
                <Typography variant="body2" color="textSecondary">
                  Drinks
                </Typography>
                <div className={styles.drinksChips}>
                  {rider.hospitality.drinks.map((drink, i) => (
                    <Chip key={i} size="small" label={drink} />
                  ))}
                </div>
              </div>
            ) : null}
            {rider.hospitality.notes ? (
              <div className={styles.notesSection}>
                <Typography variant="body2" color="textSecondary">
                  Notes
                </Typography>
                <Typography>{rider.hospitality.notes}</Typography>
              </div>
            ) : null}
          </div>
        </Paper>
      ) : null}
    </div>
  )
}

export default RiderContent
